import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from "@repo/ui";
import {
  CheckCircle2,
  Circle,
  Building2,
  Users,
  FileCheck,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";

interface SetupTask {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  icon: React.ReactNode;
  action?: () => void;
  actionLabel?: string;
}

interface SetupChecklistProps {
  tasks: SetupTask[];
  completedCount: number;
  totalCount: number;
  variant?: "card" | "minimal";
}

export function SetupChecklist({
  tasks,
  completedCount,
  totalCount,
  variant = "card",
}: SetupChecklistProps) {
  const progress = (completedCount / totalCount) * 100;
  const isMinimal = variant === "minimal";

  const progressSummary = (
    <div className={isMinimal ? "rounded-lg border bg-muted/30 p-3" : "mt-4"}>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-primary to-primary/80"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {Math.round(progress)}% complete
      </p>
    </div>
  );

  const taskList = (
    <div className={isMinimal ? "space-y-2" : "space-y-3"}>
      {tasks.map((task, index) => (
        <motion.div
          key={task.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`flex items-start gap-3 rounded-lg border transition-colors ${
            task.completed
              ? "bg-green-50 border-green-200"
              : isMinimal
                ? "bg-background border-border hover:border-primary/40"
                : "bg-muted/30 border-border hover:bg-muted/50"
          } ${isMinimal ? "p-2.5" : "p-3"}`}
        >
          <div className="flex-shrink-0 mt-0.5">
            {task.completed ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-muted-foreground">{task.icon}</div>
                  <h4
                    className={`text-sm font-medium ${
                      task.completed
                        ? "text-green-900 line-through"
                        : "text-foreground"
                    }`}
                  >
                    {task.title}
                  </h4>
                </div>
                <p
                  className={`text-xs ${
                    task.completed ? "text-green-700" : "text-muted-foreground"
                  }`}
                >
                  {task.description}
                </p>
              </div>

              {!task.completed && task.action && (
                <Button
                  onClick={task.action}
                  size="sm"
                  variant={isMinimal ? "outline" : "ghost"}
                  className={`flex-shrink-0 ${isMinimal ? "h-7 px-2 text-[11px]" : "h-8 text-xs"}`}
                >
                  {task.actionLabel || "Complete"}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const completedState = completedCount === totalCount && (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`mt-4 rounded-lg border border-green-200 ${
        isMinimal
          ? "bg-green-50 p-3"
          : "bg-gradient-to-r from-green-50 to-emerald-50 p-4 border-2"
      }`}
    >
      <div className="flex items-center gap-3">
        {!isMinimal && (
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
          </div>
        )}
        <div>
          <h4
            className={`${isMinimal ? "text-sm" : "font-semibold"} text-green-900`}
          >
            Account Activated!
          </h4>
          <p className={`${isMinimal ? "text-xs" : "text-sm"} text-green-700`}>
            Your pharmacy is now fully set up and ready to use
          </p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <Card className={isMinimal ? "border shadow-sm" : "border-2"}>
      <CardHeader className={isMinimal ? "space-y-3 pb-3" : ""}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={isMinimal ? "text-base" : "text-xl"}>
              Setup Checklist
            </CardTitle>
            {!isMinimal && (
              <CardDescription>
                Complete these tasks to activate your account
              </CardDescription>
            )}
          </div>
          <Badge
            variant={completedCount === totalCount ? "default" : "secondary"}
            className={isMinimal ? "text-xs px-2 py-0.5" : "text-sm px-3 py-1"}
          >
            {completedCount} of {totalCount} completed
          </Badge>
        </div>

        {progressSummary}
      </CardHeader>

      <CardContent className={isMinimal ? "pt-0" : ""}>
        {taskList}
        {completedState}
      </CardContent>
    </Card>
  );
}

// Export a helper to create setup tasks
export function createSetupTasks({
  hasUsers,
  hasPharmacyDetails,
  onAddUsers,
  onUpdatePharmacyDetails,
}: {
  hasUsers: boolean;
  hasPharmacyDetails: boolean;
  onAddUsers: () => void;
  onUpdatePharmacyDetails: () => void;
}): SetupTask[] {
  return [
    {
      id: "pharmacy-details",
      title: "Complete Pharmacy Details",
      description: "Add your DEA number, license, and NCPDP for compliance",
      completed: hasPharmacyDetails,
      icon: <Building2 className="h-4 w-4" />,
      action: hasPharmacyDetails ? undefined : onUpdatePharmacyDetails,
      actionLabel: "Add Details",
    },
    {
      id: "add-users",
      title: "Add Team Members",
      description: "Invite pharmacists and technicians to your team",
      completed: hasUsers,
      icon: <Users className="h-4 w-4" />,
      action: hasUsers ? undefined : onAddUsers,
      actionLabel: "Add Users",
    },
    {
      id: "verify-setup",
      title: "Review & Verify",
      description: "Confirm all information is accurate",
      completed: hasUsers && hasPharmacyDetails,
      icon: <FileCheck className="h-4 w-4" />,
    },
  ];
}
